import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaTimes, FaMapMarkerAlt, FaUser, FaPhone, FaEnvelope,
  FaCheckCircle, FaArrowLeft, FaGoogle, FaCreditCard,
  FaMoneyBillWave, FaCopy, FaCheck, FaTag,
  FaTruck,
} from 'react-icons/fa';
import { GiKnifeFork } from 'react-icons/gi';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import './CheckoutModal.css';

const STEPS = ['details', 'address', 'payment', 'confirm'];

const COURSE_MEAL_PRICE = 25.00;
const DEFAULT_DELIVERY_FEE = 2.50;

const BANK_DETAILS = {
  iban: 'IE29AIBK93226459905013',
  bic: 'AIBKIE2D',
  account: '59905013',
  nsc: '932264',
  name: 'King Kebab',
};

const PAYMENT_METHODS = [
  {
    id: 'card',
    label: 'Card',
    desc: 'Pay by debit or credit card',
    icon: <FaCreditCard />,
  },
  {
    id: 'revolut',
    label: 'Revolut',
    desc: 'Send via Revolut app',
    icon: <FaCreditCard />,
  },
  {
    id: 'cash_on_delivery',
    label: 'Cash on Delivery',
    desc: 'Pay when your order arrives',
    icon: <FaMoneyBillWave />,
  },
];

const CheckoutModal = ({ isOpen, onClose, onOpenAuth }) => {
  const navigate = useNavigate();
  const { items, clearCart } = useCart();
  const { user, isLoggedIn } = useAuth();
  const [step, setStep] = useState('details');
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [error, setError] = useState('');

  const [customer, setCustomer] = useState({ name: '', email: '', phone: '' });
  const [address, setAddress] = useState({ street: '', city: '', eircode: '', notes: '' });
  const [orderType, setOrderType] = useState('delivery');
  const [paymentMethod, setPaymentMethod] = useState('cash_on_delivery');
  const [copiedField, setCopiedField] = useState('');

  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [storeDeliveryFee, setStoreDeliveryFee] = useState(DEFAULT_DELIVERY_FEE);

  // Pre-fill from logged-in user
  useEffect(() => {
    if (isLoggedIn && user) {
      setCustomer({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
      const def = user.savedAddresses?.find(a => a.isDefault) || user.savedAddresses?.[0];
      if (def) {
        setAddress({
          street: def.street || '',
          city: def.city || '',
          eircode: def.eircode || '',
          notes: '',
        });
      }
    }
  }, [isLoggedIn, user, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchCheckoutData = async () => {
      try {
        const [couponsRes, settingsRes] = await Promise.all([
          axios.get('http://localhost:5000/api/coupons/active'),
          axios.get('http://localhost:5000/api/settings'),
        ]);
        if (couponsRes.data?.success) setAvailableCoupons(couponsRes.data.data || []);
        if (settingsRes.data?.success && settingsRes.data.data?.deliveryFee !== undefined) {
          setStoreDeliveryFee(settingsRes.data.data.deliveryFee);
        }
      } catch (err) {
        setAvailableCoupons([]);
      }
    };
    fetchCheckoutData();
  }, [isOpen]);

  /* ─── Pricing ─── */
  const subtotal = COURSE_MEAL_PRICE;
  const discountAmount = appliedCoupon
    ? Number(appliedCoupon.discount ?? (
      appliedCoupon.type === 'percent'
        ? (subtotal * appliedCoupon.value) / 100
        : Math.min(appliedCoupon.value, subtotal)
    ))
    : 0;
  const effectiveDeliveryFee = orderType === 'takeaway' ? 0 : storeDeliveryFee;
  const total = Math.max(0, subtotal + effectiveDeliveryFee - discountAmount);

  /* ─── Coupon ─── */
  const applyCoupon = async (manualCode) => {
    setCouponError('');
    const code = (manualCode || couponInput).trim().toUpperCase();
    if (!code) return;
    try {
      setCouponLoading(true);
      const res = await axios.post('http://localhost:5000/api/coupons/validate', {
        code,
        orderTotal: subtotal,
      });
      if (res.data?.success) {
        setAppliedCoupon(res.data.data);
        setCouponInput(code);
        setCouponError('');
      }
    } catch (err) {
      setCouponError(err.response?.data?.message || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  /* ─── Copy to clipboard ─── */
  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 2000);
    });
  };

  const handleCustomerChange = (e) => setCustomer({ ...customer, [e.target.name]: e.target.value });
  const handleAddressChange = (e) => setAddress({ ...address, [e.target.name]: e.target.value });

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep('details');
      setError('');
      setOrderId(null);
      setOrderType('delivery');
      setPaymentMethod('cash_on_delivery');
      setCopiedField('');
      setAppliedCoupon(null);
      setCouponInput('');
      setCouponError('');
    }, 400);
  };

  const goToTracking = () => {
    if (!orderId) return;
    handleClose();
    navigate(`/track/${orderId}`);
  };

  const submitOrder = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        customer,
        items: items.map((i) => {
          const isStaticId = /^[a-z]+\d+$/.test(i._id);
          return {
            ...(isStaticId ? {} : { menuItem: i._id }),
            name: i.name,
            price: i.price,
            quantity: i.quantity,
          };
        }),
        subtotal,
        deliveryFee: effectiveDeliveryFee,
        total,
        paymentMethod,
        orderType,
        deliveryAddress: orderType === 'takeaway'
          ? { street: '', city: '', eircode: '', notes: '' }
          : address,
        couponCode: appliedCoupon?.code || '',
        discount: discountAmount,
      };

      const res = await axios.post('http://localhost:5000/api/orders', payload);
      if (res.data.success) {
        if (appliedCoupon?.code) {
          axios.post('http://localhost:5000/api/coupons/use', { code: appliedCoupon.code }).catch(() => {});
        }
        setOrderId(res.data.data._id);
        setStep('confirm');
        clearCart();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDetailsNext = (e) => {
    e.preventDefault();
    setStep(orderType === 'takeaway' ? 'payment' : 'address');
  };

  const handleAddressNext = (e) => {
    e.preventDefault();
    setStep('payment');
  };

  const handlePaymentNext = () => {
    submitOrder();
  };

  const stepIndex = STEPS.indexOf(step);
  const totalSteps = orderType === 'takeaway' ? 2 : 3; // confirm is the success screen

  if (!isOpen) return null;

  return (
    <div className="checkout-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="checkout-modal" id="checkout-modal">
        {/* Header */}
        <div className="checkout-header">
          <div className="checkout-header-left">
            {(step === 'address' || step === 'payment') && (
              <button className="checkout-back-btn" onClick={() => setStep(step === 'payment' ? (orderType === 'takeaway' ? 'details' : 'address') : 'details')}>
                <FaArrowLeft />
              </button>
            )}
            <div>
              <span className="checkout-eyebrow">
                {step === 'details' && (orderType === 'takeaway' ? 'Step 1 of 2' : 'Step 1 of 3')}
                {step === 'address' && 'Step 2 of 3'}
                {step === 'payment' && (orderType === 'takeaway' ? 'Step 2 of 2' : 'Step 3 of 3')}
                {step === 'confirm' && 'Order Confirmed!'}
              </span>
              <h3 className="checkout-title">
                {step === 'details' && 'Your Details'}
                {step === 'address' && 'Delivery Address'}
                {step === 'payment' && 'Payment Method'}
                {step === 'confirm' && 'Thank You'}
              </h3>
            </div>
          </div>
          <button className="checkout-close-btn" onClick={handleClose} id="checkout-close-btn">
            <FaTimes />
          </button>
        </div>

        {/* Progress Bar */}
        {step !== 'confirm' && (
          <div className="checkout-progress">
            <div className="checkout-progress-fill" style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }} />
          </div>
        )}

        {/* Body */}
        <div className="checkout-body">

          {/* STEP 1: Customer Details */}
          {step === 'details' && (
            <form className="checkout-form" onSubmit={handleDetailsNext} id="checkout-details-form">
              <div className="checkout-order-summary">
                <h4 className="co-summary-title">Order Summary</h4>
                {items.map((item) => (
                  <div className="co-summary-row" key={item._id}>
                    <span>{item.name}</span>
                    <span className="co-included">included</span>
                  </div>
                ))}
                <div className="co-summary-row co-meal-price">
                  <span>Course Meal</span>
                  <span>€{COURSE_MEAL_PRICE.toFixed(2)}</span>
                </div>
                <div className="co-summary-row co-delivery">
                  <span><FaTruck /> {orderType === 'takeaway' ? 'Pickup Fee' : 'Delivery Fee'}</span>
                  <span>€{effectiveDeliveryFee.toFixed(2)}</span>
                </div>
                {appliedCoupon && (
                  <div className="co-summary-row co-discount">
                    <span><FaTag /> {appliedCoupon.label}</span>
                    <span>-€{discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="co-summary-total">
                  <span>Total</span>
                  <span>€{total.toFixed(2)}</span>
                </div>
              </div>

              {/* Coupon Code */}
              <div className="coupon-section">
                <label className="cf-label"><FaTag /> Coupon Code</label>
                {!appliedCoupon && availableCoupons.length > 0 && (
                  <div className="coupon-available-list">
                    {availableCoupons.map((coupon) => (
                      <button
                        key={coupon.code}
                        type="button"
                        className="coupon-chip"
                        onClick={() => applyCoupon(coupon.code)}
                        disabled={couponLoading}
                      >
                        <span>{coupon.code}</span>
                        <small>
                          {coupon.label}
                          {coupon.minOrderValue > 0 ? ` (min €${Number(coupon.minOrderValue).toFixed(2)})` : ''}
                        </small>
                      </button>
                    ))}
                  </div>
                )}
                {appliedCoupon ? (
                  <div className="coupon-applied">
                    <span className="coupon-badge"><FaCheck /> {appliedCoupon.code} — {appliedCoupon.label}</span>
                    <button type="button" className="coupon-remove" onClick={removeCoupon}><FaTimes /></button>
                  </div>
                ) : (
                  <div className="coupon-input-row">
                    <input type="text" className="cf-input coupon-input" placeholder="Enter code"
                      value={couponInput} onChange={e => setCouponInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), applyCoupon())} />
                    <button type="button" className="coupon-apply-btn" onClick={applyCoupon} disabled={couponLoading}>
                      {couponLoading ? 'Checking...' : 'Apply'}
                    </button>
                  </div>
                )}
                {couponError && <span className="coupon-error">{couponError}</span>}
              </div>

              <div className="checkout-fields">
                <div className="cf-group">
                  <label className="cf-label">Order Type</label>
                  <div className="order-type-row">
                    <button
                      type="button"
                      className={`order-type-btn ${orderType === 'delivery' ? 'active' : ''}`}
                      onClick={() => setOrderType('delivery')}
                    >
                      Delivery
                    </button>
                    <button
                      type="button"
                      className={`order-type-btn ${orderType === 'takeaway' ? 'active' : ''}`}
                      onClick={() => setOrderType('takeaway')}
                    >
                      Takeaway
                    </button>
                  </div>
                </div>
                <div className="cf-group">
                  <label className="cf-label" htmlFor="co-name"><FaUser /> Full Name</label>
                  <input id="co-name" name="name" type="text" className="cf-input"
                    placeholder="John Smith" value={customer.name} onChange={handleCustomerChange} required />
                </div>
                <div className="cf-group">
                  <label className="cf-label" htmlFor="co-email"><FaEnvelope /> Email</label>
                  <input id="co-email" name="email" type="email" className="cf-input"
                    placeholder="john@example.com" value={customer.email} onChange={handleCustomerChange} required />
                </div>
                <div className="cf-group">
                  <label className="cf-label" htmlFor="co-phone"><FaPhone /> Phone</label>
                  <input id="co-phone" name="phone" type="tel" className="cf-input"
                    placeholder="+353 90 000 0000" value={customer.phone} onChange={handleCustomerChange} required />
                </div>
              </div>

              {!isLoggedIn && (
                <div className="checkout-signin-prompt">
                  <span>Have an account? </span>
                  <button type="button" className="csp-btn" onClick={() => { onClose(); onOpenAuth && onOpenAuth(); }}>
                    <FaGoogle /> Sign in to save order history
                  </button>
                </div>
              )}

              <button type="submit" className="btn-primary checkout-next-btn" id="checkout-details-next">
                {orderType === 'takeaway' ? <FaCreditCard /> : <FaMapMarkerAlt />}
                <span>{orderType === 'takeaway' ? 'Continue to Payment' : 'Continue to Address'}</span>
              </button>
            </form>
          )}

          {/* STEP 2: Delivery Address */}
          {step === 'address' && orderType === 'delivery' && (
            <form className="checkout-form" onSubmit={handleAddressNext} id="checkout-address-form">
              <div className="checkout-fields">
                <div className="cf-group">
                  <label className="cf-label" htmlFor="co-street"><FaMapMarkerAlt /> Street Address</label>
                  <input id="co-street" name="street" type="text" className="cf-input"
                    placeholder="10 Main Street" value={address.street} onChange={handleAddressChange} required />
                </div>
                <div className="cf-row">
                  <div className="cf-group">
                    <label className="cf-label" htmlFor="co-city">City / Town</label>
                    <input id="co-city" name="city" type="text" className="cf-input"
                      placeholder="Athlone" value={address.city} onChange={handleAddressChange} required />
                  </div>
                  <div className="cf-group">
                    <label className="cf-label" htmlFor="co-eircode">Eircode (optional)</label>
                    <input id="co-eircode" name="eircode" type="text" className="cf-input"
                      placeholder="N37 XXXX" value={address.eircode} onChange={handleAddressChange} />
                  </div>
                </div>
                <div className="cf-group">
                  <label className="cf-label" htmlFor="co-notes">Delivery Notes (optional)</label>
                  <textarea id="co-notes" name="notes" className="cf-input cf-textarea"
                    placeholder="Apartment number, gate code, landmark..." value={address.notes}
                    onChange={handleAddressChange} rows={3} />
                </div>
              </div>

              {error && <div className="checkout-error">{error}</div>}

              <button type="submit" className="btn-primary checkout-next-btn" id="checkout-address-next">
                <FaCreditCard />
                <span>Continue to Payment</span>
              </button>
            </form>
          )}

          {/* STEP 3: Payment Method Selection */}
          {step === 'payment' && (
            <div className="checkout-form">
              {/* Payment method cards */}
              <div className="payment-methods">
                {PAYMENT_METHODS.map((method) => (
                  <button
                    key={method.id}
                    className={`payment-method-card ${paymentMethod === method.id ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod(method.id)}
                    type="button"
                    id={`payment-${method.id}`}
                  >
                    <span className="pmc-icon">{method.icon}</span>
                    <div className="pmc-info">
                      <span className="pmc-label">{method.label}</span>
                      <span className="pmc-desc">{method.desc}</span>
                    </div>
                    <span className={`pmc-radio ${paymentMethod === method.id ? 'checked' : ''}`} />
                  </button>
                ))}
              </div>

              {/* Card Details */}
              {paymentMethod === 'card' && (
                <div className="bank-details-card">
                  <h4 className="bank-title"><FaCreditCard /> Card Payment</h4>
                  <p className="bank-desc">You selected card payment. We will process this order as a card payment request.</p>
                  <div className="bank-ref-note">
                    <strong>Payment method:</strong> Card
                  </div>
                </div>
              )}

              {/* Revolut Details */}
              {paymentMethod === 'revolut' && (
                <div className="bank-details-card revolut-card">
                  <h4 className="bank-title"><FaCreditCard /> Pay via Revolut</h4>
                  <p className="bank-desc">Send the total amount via Revolut to our account below. Your order will be confirmed once payment is received.</p>

                  <div className="bank-rows">
                    <div className="bank-row">
                      <span className="bank-label">Revolut Name</span>
                      <div className="bank-value-row">
                        <span className="bank-value">{BANK_DETAILS.name}</span>
                        <button className="bank-copy" onClick={() => copyToClipboard(BANK_DETAILS.name, 'rev-name')}>
                          {copiedField === 'rev-name' ? <FaCheck /> : <FaCopy />}
                        </button>
                      </div>
                    </div>
                    <div className="bank-row">
                      <span className="bank-label">IBAN</span>
                      <div className="bank-value-row">
                        <span className="bank-value mono">{BANK_DETAILS.iban}</span>
                        <button className="bank-copy" onClick={() => copyToClipboard(BANK_DETAILS.iban, 'rev-iban')}>
                          {copiedField === 'rev-iban' ? <FaCheck /> : <FaCopy />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bank-ref-note">
                    <strong>Reference:</strong> Use your phone number as the payment reference
                  </div>
                </div>
              )}

              {/* Cash on Delivery */}
              {paymentMethod === 'cash_on_delivery' && (
                <div className="bank-details-card cod-card">
                  <h4 className="bank-title"><FaMoneyBillWave /> Cash on Delivery</h4>
                  <p className="bank-desc">
                    {orderType === 'takeaway'
                      ? 'Pay with cash when you collect your takeaway order at the restaurant.'
                      : 'Pay with cash when your order is delivered to your door. Please have the exact amount ready if possible.'}
                  </p>
                  <div className="cod-total">
                    <span>{orderType === 'takeaway' ? 'Amount Due on Pickup' : 'Amount Due on Delivery'}</span>
                    <span className="cod-amount">€{total.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Order Total */}
              <div className="payment-total-box">
                <div className="ptb-row"><span>Subtotal</span><span>€{subtotal.toFixed(2)}</span></div>
                <div className="ptb-row"><span>{orderType === 'takeaway' ? 'Pickup Fee' : 'Delivery Fee'}</span><span>€{effectiveDeliveryFee.toFixed(2)}</span></div>
                {appliedCoupon && (
                  <div className="ptb-row ptb-discount"><span><FaTag /> {appliedCoupon.code}</span><span>-€{discountAmount.toFixed(2)}</span></div>
                )}
                <div className="ptb-total"><span>Total</span><span>€{total.toFixed(2)}</span></div>
              </div>

              {error && <div className="checkout-error">{error}</div>}

              <button
                type="button"
                className={`btn-primary checkout-next-btn ${loading ? 'loading' : ''}`}
                disabled={loading}
                onClick={handlePaymentNext}
                id="checkout-place-order-btn"
              >
                {loading ? (
                  <><span className="spinner" /><span>Placing Order...</span></>
                ) : (
                  <><GiKnifeFork /><span>Place Order — €{total.toFixed(2)}</span></>
                )}
              </button>
            </div>
          )}

          {/* STEP 4: Order Confirmation */}
          {step === 'confirm' && (
            <div className="checkout-payment-placeholder">
              <div className="payment-success-icon">
                <FaCheckCircle />
              </div>
              <h3 className="payment-success-title">Order Placed!</h3>
              {orderId && (
                <p className="payment-order-id">Order ID: <span>#{orderId.slice(-8).toUpperCase()}</span></p>
              )}

              <div className="confirm-method-badge">
                {paymentMethod === 'card' && <><FaCreditCard /> Card</>}
                {paymentMethod === 'revolut' && <><FaCreditCard /> Revolut</>}
                {paymentMethod === 'cash_on_delivery' && <><FaMoneyBillWave /> Cash on Delivery</>}
              </div>

              <p className="payment-success-sub">
                {orderType === 'takeaway'
                  ? 'Your takeaway order has been received. We will update the status once it is ready for pickup.'
                  : paymentMethod === 'cash_on_delivery'
                    ? 'Your order has been received! Please have €' + total.toFixed(2) + ' ready when the delivery arrives.'
                    : 'Your order has been received! Please complete the payment of €' + total.toFixed(2) + ' using the details provided. We\'ll confirm once payment is received.'}
              </p>

              {appliedCoupon && (
                <p className="confirm-coupon">
                  <FaTag /> Coupon <strong>{appliedCoupon.code}</strong> applied — you saved €{discountAmount.toFixed(2)}!
                </p>
              )}

              <button className="btn-outline payment-close-btn" onClick={handleClose} id="payment-done-btn">
                Done
              </button>
              {orderId && (
                <button className="btn-primary payment-close-btn" onClick={goToTracking} type="button">
                  <FaTruck />
                  <span>Track Order</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
